import React, { useEffect, useState, useCallback } from "react";
import { Typography, Table, Button, Stack } from "@mui/joy";
import { bytesToMegabytes } from "../../util/objectUtil";

export type FSItem = {
    name: string;
    kind: "file" | "directory";
    size?: number;
    lastModified?: number;
};

export default function NavigatorStorageViewer() {
    const [items, setItems] = useState<FSItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const refresh = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            if (!("storage" in navigator) || !navigator.storage.getDirectory) {
                setError("OPFS is not supported in this browser.");
                setItems([]);
                return;
            }

            const root = await navigator.storage.getDirectory();
            const list: FSItem[] = [];

            // @ts-ignore
            for await (const [name, handle] of root.entries() as any) {
                if (handle.kind === "file") {
                    const fileHandle = handle as FileSystemFileHandle;
                    const file = await fileHandle.getFile();
                    list.push({
                        name,
                        kind: "file",
                        size: file.size,
                        lastModified: file.lastModified,
                    });
                } else {
                    list.push({ name, kind: "directory" });
                }
            }

            setItems(list);
        } catch (err: any) {
            setError(err?.message ?? "Unknown error");
        } finally {
            setLoading(false);
        }
    }, []);

    const deleteEntry = useCallback(
        async (name: string, kind: FSItem["kind"]) => {
            setLoading(true);
            setError(null);
            try {
                const root = await navigator.storage.getDirectory();
                // @ts-ignore
                await root.removeEntry(name, { recursive: kind === "directory" });
                await refresh();
            } catch (err: any) {
                setError(err?.message ?? "Error deleting entry");
            } finally {
                setLoading(false);
            }
        },
        [refresh],
    );

    useEffect(() => {
        void refresh();
    }, [refresh]);

    const fmtDate = (ms?: number) =>
        ms ? new Date(ms).toLocaleString(undefined, { hour12: false }) : "-";

    return (
        <Stack sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between">
                <Typography>
                    <Typography level="h4" className="mb-4">
                        Navigator Storage Viewer
                    </Typography>
                    <br />
                    <Typography level="body-sm">
                        These are the files cached on your disk by this app. This only works on
                        Chrome and Edge.
                    </Typography>
                </Typography>
                <Button onClick={refresh} loading={loading} variant="outlined">
                    Refresh
                </Button>
            </Stack>

            {error && (
                <Typography color="danger" className="mt-2">
                    {error}
                </Typography>
            )}
            <Table aria-label="navigator-storage" className="mt-4" stickyHeader>
                <thead>
                    <tr>
                        <th>Name</th>
                        <th>Kind</th>
                        <th>Size (MB)</th>
                        <th>Last modified</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {items.length === 0 && !loading && (
                        <tr>
                            <td colSpan={5}>No entries</td>
                        </tr>
                    )}
                    {items.map((item) => (
                        <tr key={item.name}>
                            <td>{item.name}</td>
                            <td>{item.kind}</td>
                            <td>{bytesToMegabytes(item.size ?? 0)}</td>
                            <td>{item.kind === "file" ? fmtDate(item.lastModified) : "-"}</td>
                            <td>
                                <Button
                                    variant="outlined"
                                    color="danger"
                                    size="sm"
                                    onClick={() => deleteEntry(item.name, item.kind)}
                                    disabled={loading}
                                >
                                    Delete
                                </Button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </Stack>
    );
}
